import React from "react";

// Catch any uncaught render error in the React tree and render a useful
// message instead of going white. Without this, React 18's default
// behavior on an uncaught error is to unmount the whole tree — operator
// sees a blank page with no clue what to do.
//
// Added 2026-05-24 PT after Terrell reported "the dashboard link doesnt
// load it flashes and then turns white" — we still don't know the root
// cause; the boundary at minimum surfaces the error so it can be fixed
// instead of silently blanking the page.

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

interface Props {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Browser console gets the full trace for devs; UI gets the summary.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
    this.setState({ error, info });
  }

  private reset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#0b0b0b",
          color: "#f4f4f5",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          padding: "32px",
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <h1 style={{ color: "#f43f5e", fontSize: 20, marginBottom: 8 }}>
            Dashboard hit an error.
          </h1>
          <p style={{ color: "#a1a1aa", fontSize: 13, marginBottom: 16 }}>
            The page caught an exception instead of going white. Share the
            block below with Sassy so she can fix it. Try a hard refresh
            (Cmd+Shift+R) first — most often this is a stale browser cache
            pointing at a deleted bundle.
          </p>
          <pre
            style={{
              backgroundColor: "#18181b",
              border: "1px solid #27272a",
              padding: 16,
              borderRadius: 6,
              overflow: "auto",
              fontSize: 12,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <strong style={{ color: "#fbbf24" }}>{error.name}: {error.message}</strong>
            {"\n\n"}
            {error.stack || "(no stack)"}
            {info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : ""}
          </pre>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: "#27272a",
                color: "#fafafa",
                border: "1px solid #3f3f46",
                padding: "8px 14px",
                fontSize: 12,
                cursor: "pointer",
                borderRadius: 4,
              }}
            >
              Reload page
            </button>
            <button
              onClick={this.reset}
              style={{
                backgroundColor: "transparent",
                color: "#a1a1aa",
                border: "1px solid #3f3f46",
                padding: "8px 14px",
                fontSize: 12,
                cursor: "pointer",
                borderRadius: 4,
              }}
            >
              Try to recover without reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
